import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatGrade, getParticipantPalette } from './fullDuplexModel';

function prepareMarkers(markers = []) {
  const lanes = new Map();
  return markers.map((marker) => {
    const key = Number(marker.score).toFixed(2);
    const lane = lanes.get(key) || 0;
    lanes.set(key, lane + 1);
    return { ...marker, lane };
  });
}

function GradeBar({ summary, palette, onMarkerClick, meanLabel = 'Mittelwert' }) {
  const percent = (score) => ((Number(score) - 1) / 5) * 100;
  const markers = prepareMarkers(summary?.markers || []);
  const stackDepth = markers.reduce((maximum, marker) => Math.max(maximum, marker.lane + 1), 0);
  const barTop = 13 + stackDepth * 6;

  return (
    <Box sx={{ position: 'relative', pt: `${barTop}px`, pb: 2.5 }}>
      <Box sx={{ height: 10, borderRadius: 999, background: 'linear-gradient(90deg, #2E7D32, #E9B522 50%, #C62828)' }} />
      {markers.map((marker) => (
        <Tooltip key={`${marker.submissionId}-${marker.score}`} title={`${marker.label}: ${formatGrade(marker.score)}`} arrow>
          <Box
            component="button"
            type="button"
            aria-label={`${marker.label}: ${formatGrade(marker.score)}`}
            onClick={() => onMarkerClick?.(marker.submissionId)}
            sx={{
              appearance: 'none',
              p: 0,
              cursor: onMarkerClick ? 'pointer' : 'default',
              position: 'absolute',
              zIndex: 2,
              top: barTop - 15 - marker.lane * 6,
              left: `calc(${percent(marker.score)}% - 6px)`,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: palette[Number(marker.submissionId)] || 'secondary.main',
              border: '2px solid white',
              boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.45)',
            }}
          />
        </Tooltip>
      ))}
      {summary?.average != null && (
        <Tooltip title={`${meanLabel}: ${formatGrade(summary.average)}`} arrow>
          <Box
            component="span"
            sx={{
              position: 'absolute',
              zIndex: 3,
              top: barTop - 3,
              left: `calc(${percent(summary.average)}% - 8px)`,
              width: 16,
              height: 16,
              borderRadius: '50%',
              bgcolor: 'white',
              border: '3px solid',
              borderColor: 'primary.main',
            }}
          />
        </Tooltip>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.6 }}>
        {[1, 2, 3, 4, 5, 6].map((grade) => <Typography key={grade} variant="caption">{grade}</Typography>)}
      </Stack>
    </Box>
  );
}

function ResultCard({ title, summary, palette, onMarkerClick }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {summary?.responseCount || 0} Bewertung{summary?.responseCount === 1 ? '' : 'en'} berücksichtigt
        </Typography>
        <GradeBar summary={summary} palette={palette} onMarkerClick={onMarkerClick} />
      </CardContent>
    </Card>
  );
}

export default function FullDuplexResults({
  config,
  analysis,
  submissions = [],
  onMarkerClick,
  showLegend = false,
}) {
  const palette = useMemo(() => getParticipantPalette(submissions), [submissions]);
  if (!analysis?.submissionCount) {
    return <Alert severity="info">Noch keine Bewertungen vorhanden.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      {showLegend && (
        <Alert severity="info" icon={false}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'white', border: '3px solid', borderColor: 'primary.main' }} />
              <Typography variant="body2">Gruppenmittel</Typography>
            </Stack>
            {submissions.map((submission) => (
              <Stack key={submission.id} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: palette[submission.id], border: '2px solid white', boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.45)' }} />
                <Typography variant="body2">{submission.userName}</Typography>
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}

      {(config?.deviceGroups || []).map((group) => (
        <Stack key={group.key} spacing={1.25}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{group.label}</Typography>
          <ResultCard title={`Gerätemittel: ${formatGrade(analysis.devices?.[group.key]?.average)}`} summary={analysis.devices?.[group.key]} palette={palette} onMarkerClick={onMarkerClick} />
          <Grid container spacing={1.25}>
            {group.criteria.map((criterion) => (
              <Grid key={criterion.key} size={{ xs: 12, md: 6 }}>
                <ResultCard title={criterion.label} summary={analysis.criteria?.[criterion.key]} palette={palette} onMarkerClick={onMarkerClick} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      ))}
    </Stack>
  );
}
